/**
 * @file ReceiptDocHeader.tsx
 * @description Шапка документа «Приёмка»: номер, статус, плитки meta и
 * действия (phase19.24 этап 1, шаг 1; phase19.31 зона «Склад» — шапка в
 * верхней панели, `WmsDocumentPageHeader`).
 *
 * Действий у приёмки два состояния: черновик проводим («Провести приёмку»),
 * проведённую раскладываем по ячейкам. Одна главная кнопка; печать этикеток
 * и звук скана — в «⋯». На телефоне главная кнопка — в общей нижней полосе
 * каркаса, причина блокировки — подписью над ней (раньше — своя липкая
 * полоса документа). Проведённая приёмка без права размещения, но с
 * непечатанными этикетками: главная кнопка — «Печать этикеток (N)».
 */

import { Check, Printer } from "lucide-react";

import { documentCommitHint, placementBlockedHint } from "./document-texts";
import { ReceiptPlacementButton } from "./transfers/ReceiptPlacementButton";
import { useScanSoundAction } from "./useScanSoundAction";
import { WmsDocumentPageHeader } from "./WmsDocumentPageHeader";
import { buildDocumentMeta } from "./wms-document-meta";
import { Button } from "../ui/button";
import { StatusPill } from "../ui/StatusPill";
import type { GoodsReceiptDto } from "../../lib/api-wms";
import { RECEIPT_STATUS_META } from "../../lib/shop/status-meta";

interface Props {
  receipt: GoodsReceiptDto;
  /** Реестр приёмок — «назад». */
  backTo: string;
  /** Право на лист размещения проведённой приёмки (OQ-46). */
  canPlace: boolean;
  committing: boolean;
  onCommit: () => void;
  /** phase19.22 — печать этикеток (у черновика пункт «⋯» неактивен, W17-5a). */
  onPrintLabels?: () => void;
  /** Позиций к печати этикеток (не печатались + устарели). */
  pendingLabels?: number;
  /** Почему проводить нельзя (блок, решение 2026-09-26); `null` — можно. */
  blockReason?: string | null;
}

/** «Основной · ООО Травы · накладная 15». */
function receiptSubtitle(receipt: GoodsReceiptDto): string {
  return [
    receipt.warehouse?.name,
    receipt.supplier?.displayName,
    receipt.vendorDocNumber ? `накладная ${receipt.vendorDocNumber}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function ReceiptDocHeader({
  receipt,
  backTo,
  canPlace,
  committing,
  onCommit,
  onPrintLabels,
  pendingLabels = 0,
  blockReason = null,
}: Props) {
  const isDraft = receipt.status === "DRAFT";
  const items = receipt.items ?? [];
  const hint = documentCommitHint("receipt", items.length);
  // Проведённая приёмка: адрес хранения принадлежит партии, поэтому приёмку
  // целиком без партий раскладывать нечем (решение пользователя 2026-09-20).
  const placementHint = placementBlockedHint(receipt.itemsWithoutLot, items.length);
  const scanSound = useScanSoundAction();
  // Печать этикеток — после проведения (W17-5a): у черновика пункт виден, но
  // неактивен и подписан — кладовщик знает, где он появится.
  const printLabels = onPrintLabels
    ? {
        key: "labels",
        label: isDraft ? "Печать этикеток — после проведения" : "Печать этикеток",
        icon: Printer,
        disabled: isDraft,
        run: onPrintLabels,
      }
    : null;
  // W20 «мягко»: строки без срока не блокируют проведение — только подпись.
  const missing = receipt.missingExpiryItems?.length ?? 0;
  const expiryNote = missing > 0 ? `${missing} без срока годности` : null;

  const action = isDraft ? (
    <Button variant="brand" onClick={onCommit} disabled={!!hint || !!blockReason || committing} loading={committing}>
      <Check className="size-4" />
      Провести приёмку
    </Button>
  ) : canPlace ? (
    // Проведённая приёмка — точка входа в лист размещения (phase19.23).
    <ReceiptPlacementButton receiptId={receipt.id} disabled={!!placementHint} />
  ) : onPrintLabels && pendingLabels > 0 ? (
    <Button variant="outline" onClick={onPrintLabels}>
      <Printer className="size-4" />
      Печать этикеток ({pendingLabels} поз.)
    </Button>
  ) : undefined;

  return (
    <WmsDocumentPageHeader
      number={receipt.receiptNumber}
      status={<StatusPill meta={RECEIPT_STATUS_META[receipt.status]} />}
      subtitle={receiptSubtitle(receipt)}
      back={{ to: backTo, label: "К приёмкам" }}
      meta={buildDocumentMeta({
        authorLabel: "Принял",
        author: receipt.receivedBy?.displayName,
        createdAt: receipt.createdAt,
        committedAt: receipt.committedAt,
        items,
      })}
      action={action}
      menu={printLabels ? [printLabels, scanSound] : [scanSound]}
      menuLabel="Действия с приёмкой"
      hint={isDraft ? (hint ?? blockReason ?? expiryNote) : canPlace ? placementHint : null}
    />
  );
}
